import { useCallback } from 'react';
import { useCompany } from 'src/app/Company.store';
import { useGetToken } from 'src/api/config';
import { getAuthPropertyId, markIdPortenLoginStarted } from 'src/api/state';
import { buildEidRedirectUrl } from 'src/api/eidRedirect';
import { selectIdPortenUrls } from 'src/api/eidProviders';

/**
 * The one place the portal sends a citizen to ID-porten.
 *
 * Two surfaces need it and must not drift apart: the `post_auth_search` button
 * (`IdPortenLogin`) and the `pre_auth_search` path in `Property.store`, which
 * redirects on its own as soon as an address is picked — the same split
 * `login_method_user` creates for BankID.
 *
 * `redirectToIdPorten` is a no-op returning `false` when the tenant has no
 * ID-porten entry in `authentication_urls` (the backend emits one iff an
 * `IdPortenApplication` row exists, so absence means "not provisioned yet").
 * Callers use the return value to show their own message rather than leaving
 * the citizen on a button that does nothing.
 *
 * `api_key` is read at click time and is the live anonymous session token, not
 * a stored credential — see `buildEidRedirectUrl`.
 */
export const useIdPortenRedirect = () => {
  const { data: company } = useCompany();
  const token = useGetToken();

  const [entry] = selectIdPortenUrls(company?.authentication_urls);

  const redirectToIdPorten = useCallback((): boolean => {
    if (!entry) {
      console.error(
        '[IdPorten] no idporten entry in authentication_urls — tenant is flagged for ID-porten but has no IdPortenApplication row'
      );
      return false;
    }
    // Record that THIS session is about to be established by ID-porten, so
    // logout() knows to end Digdir's central SSO session. Confirmed when the
    // hand-back arrives with a fresh ?api_key= (see UserStoreProvider); a
    // session the portal never started this way — the admin platform's machine
    // PropertyUser, an anonymous boot key — keeps the local-only logout.
    markIdPortenLoginStarted();
    window.location.assign(
      buildEidRedirectUrl(entry.url, token, getAuthPropertyId())
    );
    return true;
  }, [entry, token]);

  return { hasIdPortenUrl: Boolean(entry), redirectToIdPorten };
};
